import { useEffect, useState } from "react";
import { Loader2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtNum } from "@/lib/api";

const SC = [["in_scope", "In"], ["out_of_scope", "Out"], ["check", "Check"], ["future", "Future"], ["missing_code", "No code"]];
const fmt = (r) => (r ? `${r.scope} · ${r.description || "—"} · ${r.condition || "no condition"}` : "—");

const Counts = ({ c }) => (
  <table className="w-full text-sm" data-testid="annex-update-counts">
    <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400"><th className="py-1">Materials</th><th className="text-right">Before</th><th className="text-right">After</th></tr></thead>
    <tbody>{SC.map(([k, l]) => <tr key={k} className="border-t border-slate-100" data-testid={`annex-update-count-${k}`}><td className="py-1">{l}</td><td className="text-right font-mono">{fmtNum(c.before[k] || 0)}</td><td className={`text-right font-mono ${(c.before[k] || 0) !== (c.after[k] || 0) ? "font-semibold text-[#D52B3B]" : ""}`}>{fmtNum(c.after[k] || 0)}</td></tr>)}</tbody>
  </table>
);

const ChangeRow = ({ ch, ticked, onTick }) => (
  <tr className="border-t border-slate-100 align-top" data-testid={`annex-update-row-${ch.hs_code}`}>
    <td className="py-1.5 pr-2">{ch.edited && <Checkbox data-testid={`annex-update-include-${ch.hs_code}`} checked={ticked} onCheckedChange={(v) => onTick(ch.hs_code, !!v)} />}</td>
    <td className="py-1.5 pr-2 font-mono">{ch.hs_code}</td>
    <td className="py-1.5 pr-2">{ch.action === "add" ? "Add" : "Change"}{ch.edited && <span className="ml-1 rounded bg-amber-100 px-1 text-[11px] text-amber-800">edited by {ch.edited_by}</span>}</td>
    <td className="py-1.5 pr-2 text-slate-500">{fmt(ch.before)}</td>
    <td className="py-1.5">{fmt(ch.after)}</td>
  </tr>
);

function ReviewDialog({ open, onClose, onApplied }) {
  const [inc, setInc] = useState([]);
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/annex/update", { params: { include: inc.join(",") || undefined } }).then((r) => setP(r.data)); }, [open, inc]);
  const tick = (h, v) => setInc((x) => (v ? [...x, h] : x.filter((y) => y !== h)));
  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.post("/annex/update/apply", { confirm: true, include_edited: inc });
      toast.success(`Annex I update applied: ${r.data.applied.length} row(s); ${r.data.kept_edited.length} edited row(s) kept; ${fmtNum(r.data.materials_changed)} material record(s) re-evaluated.`);
      onApplied(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-5xl overflow-y-auto" data-testid="annex-update-dialog">
        <DialogHeader><DialogTitle>Review &amp; apply Annex I update v{p?.version}</DialogTitle></DialogHeader>
        {!p ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : (
          <div className="space-y-4 text-sm">
            <p className="text-slate-600" data-testid="annex-update-summary">{p.changes.length} row(s) to add or change · {p.fills.total} row(s) with empty condition / source / date to fill ({p.fills.edited} edited row(s) keep their empty condition). Rows a user edited are kept unless you tick them.</p>
            <Counts c={p.counts} />
            <table className="w-full text-xs">
              <thead><tr className="text-left uppercase tracking-wider text-slate-400"><th>Update</th><th>Code</th><th>Action</th><th>Now</th><th>After</th></tr></thead>
              <tbody>{p.changes.map((ch) => <ChangeRow key={ch.hs_code} ch={ch} ticked={inc.includes(ch.hs_code)} onTick={tick} />)}</tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" data-testid="annex-update-cancel" onClick={onClose}>Cancel</Button>
          <Button data-testid="annex-update-apply" disabled={!p || busy} onClick={apply}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Apply update</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const AnnexUpdate = ({ onApplied }) => {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/annex/update/status").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  if (!s?.available) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="annex-update-banner">
      <span>Annex I update v{s.version} is available. Nothing changes until you review and apply it.</span>
      <Button size="sm" data-testid="annex-update-review-btn" onClick={() => setOpen(true)}><RefreshCcw className="mr-1.5 h-4 w-4" />Review &amp; apply Annex I update</Button>
      <ReviewDialog open={open} onClose={() => setOpen(false)} onApplied={() => { load(); onApplied(); }} />
    </div>
  );
};
